import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useTypedParams } from '@sorare/routing';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { MissionsPageLayout } from '#src/pages/MissionsPageLayout/index.tsx';

import type {
  MissionsCategoryRarityLayoutQuery,
  MissionsCategoryRarityLayoutQueryVariables,
} from './__generated__/layout.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const MISSIONS_CATEGORY_RARITY_LAYOUT_QUERY = gql`
  query MissionsCategoryRarityLayoutQuery(
    $taskGroupSlug: String!
    $sport: Sport!
  ) {
    currentUser {
      slug
      id
      limitedCount: myUnclaimedTasksCount(
        sport: $sport
        group: $taskGroupSlug
        rarity: limited
      )
      rareCount: myUnclaimedTasksCount(
        sport: $sport
        group: $taskGroupSlug
        rarity: rare
      )
      superRareCount: myUnclaimedTasksCount(
        sport: $sport
        group: $taskGroupSlug
        rarity: super_rare
      )
      uniqueCount: myUnclaimedTasksCount(
        sport: $sport
        group: $taskGroupSlug
        rarity: unique
      )
      commonCount: myUnclaimedTasksCount(
        sport: $sport
        group: $taskGroupSlug
        rarity: common
      )
    }
  }
` as TypedDocumentNode<
  MissionsCategoryRarityLayoutQuery,
  MissionsCategoryRarityLayoutQueryVariables
>;

export const loader = queryLoader<PageParams>(
  MISSIONS_CATEGORY_RARITY_LAYOUT_QUERY,
  ({ category, sport }) => ({
    taskGroupSlug: category,
    sport,
  })
);

const MissionsCategoryLayout = () => {
  const { category, rarity } = useTypedParams<PageParams>();
  const { data } = useQueryLoader<MissionsCategoryRarityLayoutQuery>();

  const { limitedCount, rareCount, superRareCount, uniqueCount, commonCount } =
    data.currentUser || {};

  return (
    <MissionsPageLayout
      rarity={rarity}
      category={category}
      unclaimedTasksByRarity={{
        limited: limitedCount,
        rare: rareCount,
        super_rare: superRareCount,
        unique: uniqueCount,
        common: commonCount,
      }}
    />
  );
};

export default MissionsCategoryLayout;
